
import { useNavigate } from "react-router-dom";
import { StrategyWithDetails } from "@/types/strategy-types";
import { AccountPerformanceData } from "../marketplace/types/strategy-card-types";
import { useSubscriptionStatus } from "@/hooks/account/useSubscriptionStatus";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useState } from "react";
import StrategyDetailHeader from "./StrategyDetailHeader";
import StrategyDetailStats from "./StrategyDetailStats";
import { StrategyDescription } from "../strategy/StrategyDescription";

interface StrategyDetailContentProps {
  strategy: StrategyWithDetails;
  performanceData?: AccountPerformanceData;
}

const StrategyDetailContent = ({ strategy, performanceData }: StrategyDetailContentProps) => {
  const navigate = useNavigate();
  const [userId, setUserId] = useState<string | undefined>(undefined);
  const [isPhemexLanding, setIsPhemexLanding] = useState(false);
  
  // Check both "premium" and "pro" values for premium status
  const isPro = strategy?.is_pro || strategy?.plan === 'premium' || strategy?.plan === 'pro';
  
  // Get subscription status using our refactored hook
  const { subscriptionStatus } = useSubscriptionStatus(userId);
  
  // Check if the user has an active subscription
  const hasActiveSubscription = subscriptionStatus.status === "active";

  // Determine if we're on the Phemex landing page
  useEffect(() => {
    const checkIfPhemexLanding = () => {
      const isPhemex = window.location.pathname.includes('/landing/phemex');
      setIsPhemexLanding(isPhemex);
    };
    
    checkIfPhemexLanding();
  }, []);

  // Get user ID on component mount
  useEffect(() => {
    const getUser = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          setUserId(user.id);
        }
      } catch (err) {
        console.error("Error getting user:", err);
      }
    };
    
    getUser();
  }, []);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  };

  const getRiskLabel = (level: number) => {
    switch (level) {
      case 1:
        return { text: "Low", color: "text-green-500" };
      case 2:
        return { text: "Medium", color: "text-orange-500" };
      case 3:
        return { text: "High", color: "text-red-500" };
      default:
        return { text: "Unknown", color: "text-gray-500" };
    }
  };

  const handleStrategyClick = () => {
    if (!strategy) return;
    
    // Get risk label from risk level
    const riskLabelInfo = getRiskLabel(strategy.risk_level);
    
    // Format performance percentage with % symbol if it doesn't already have it
    const formattedPerformance = performanceData?.pnl_ratio !== undefined && performanceData?.pnl_ratio !== null
      ? `${performanceData.pnl_ratio.toFixed(2)}%` 
      : strategy.expected_monthly_profit || strategy.performance_percentage 
        ? `${strategy.expected_monthly_profit || strategy.performance_percentage}%`
        : "N/A";
    
    // Common state object with ALL required fields for StrategyDetails
    const commonState = {
      strategy: {
        id: strategy.id,
        name: strategy.name || strategy.strategy_name || '',
        performance: formattedPerformance,
        riskLevel: riskLabelInfo.text,
        minInvestment: `${strategy.min_investment.toLocaleString()} ${strategy.currency || 'USDT'}`,
        since: formatDate(strategy.verified_since || strategy.performance_since || ''),
        exchange: strategy.exchange || 'Unknown',
        description: strategy.description || '',
        followers: strategy.followers_count || strategy.total_followers || 0,
        aum: strategy.total_aum ?? performanceData?.aum ?? null,
        winRate: performanceData?.win_rate ?? strategy.win_rate,
        maxDrawdown: performanceData?.max_drawdown ?? strategy.max_drawdown,
        tradesWon: performanceData?.num_wins ?? strategy.trades_won,
        tradesLost: performanceData?.num_losses ?? strategy.trades_lost,
        isPremium: isPro,
        currency: strategy.currency || 'USDT'
      },
      originalStrategyId: strategy.id
    };
    
    // Direct to the correct page based on subscription status
    if (isPro && hasActiveSubscription) {
      navigate('/follow-strategy-premium-existing', {
        state: commonState
      });
    } else if (isPro) {
      navigate('/follow-strategy', {
        state: commonState
      });
    } else {
      navigate('/follow-strategy-free', {
        state: commonState
      });
    }
  };

  // Use followers_count as fallback if total_followers is not available
  const followerCount = strategy.total_followers ?? strategy.followers_count ?? 0;
  
  // Use performanceData?.aum as fallback if total_aum is not available
  const aum = strategy.total_aum ?? performanceData?.aum ?? null;
  const pnlRatio = performanceData?.pnl_ratio ?? null;
  const timespan = performanceData?.timespan ?? null;
  
  // Get other stats from performance data if available, otherwise from strategy
  const winRate = performanceData?.win_rate ?? strategy.win_rate;
  const maxDrawdown = performanceData?.max_drawdown ?? strategy.max_drawdown;
  const tradesWon = performanceData?.num_wins ?? strategy.trades_won;
  const tradesLost = performanceData?.num_losses ?? strategy.trades_lost;

  // Apply dark theme for Phemex landing page
  const bgGradient = isPhemexLanding || isPro 
    ? "bg-gradient-to-br from-[#012846] to-[#407096]" 
    : isPro 
      ? "bg-gradient-to-br from-[#012846] to-[#407096]" 
      : "bg-[#1B2F47]";

  // Create strategy data object for the follow button
  const strategyDataForButton = {
    id: strategy.strategy_id || strategy.id,
    name: strategy.name || strategy.strategy_name || '',
    performance: performanceData?.pnl_ratio !== undefined && performanceData?.pnl_ratio !== null
      ? `${performanceData.pnl_ratio.toFixed(2)}%` 
      : strategy.expected_monthly_profit || strategy.performance_percentage 
        ? `${strategy.expected_monthly_profit || strategy.performance_percentage}%`
        : "N/A",
    riskLevel: getRiskLabel(strategy.risk_level).text,
    minInvestment: `${strategy.min_investment.toLocaleString()} ${strategy.currency || 'USDT'}`,
    since: formatDate(strategy.verified_since || strategy.performance_since || ''),
    exchange: strategy.exchange || 'Unknown',
    description: strategy.description || '',
    followers: followerCount,
    aum: aum,
    winRate: winRate,
    maxDrawdown: maxDrawdown,
    tradesWon: tradesWon,
    tradesLost: tradesLost,
    isPremium: isPro,
    currency: strategy.currency || 'USDT'
  };

  return (
    <>
      <div className={`p-6 text-white ${bgGradient}`}>
        <StrategyDetailHeader 
          strategy={strategy}
          followerCount={followerCount}
          isPro={isPro}
          pnlRatio={pnlRatio}
          timespan={timespan}
        />
        
        <StrategyDetailStats 
          winRate={winRate}
          maxDrawdown={maxDrawdown}
          tradesWon={tradesWon}
          tradesLost={tradesLost}
          aum={aum}
          pnlRatio={pnlRatio}
          timespan={timespan}
          isPro={isPro}
        />
      </div>
      <StrategyDescription 
        name={strategy.name || strategy.strategy_name || ''}
        isPro={isPro}
        strategy={strategyDataForButton}
        originalStrategyId={strategy.id}
      />
    </>
  );
};

export default StrategyDetailContent;
